'use client'

// Re-fetch a server-rendered page when the student comes back to it, so it
// shows what they just did instead of what was true when they left.
//
// The dashboard is rendered on the server and carries no client-side data of
// its own, so it can only go stale by being SHOWN AGAIN without a new render:
//
//   1. Browser Back/Forward inside the app. Next replays its cached copy of
//      the page (it always reuses cache for history navigation, whatever the
//      staleTimes), so after finishing practice the dashboard still said the
//      assignment was overdue and showed no new review cards until a reload.
//      Detected without clocks: every server render passes a fresh
//      `renderId`; a page that mounts with an id this tab has already shown
//      is a replay, and gets refreshed.
//   2. The browser's own back/forward cache (a full-page restore):
//      `pageshow` with `persisted`.
//   3. Coming back to a tab or window after working somewhere else (practice
//      in a second tab): refreshed when it was away for AWAY_MS or more, so
//      quick alt-tabs don't re-render the page each time.
//
// router.refresh() re-renders the server components in place; client state
// (scroll, open panels) is kept.

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'

/** How long the page must have been out of view before a return refreshes it. */
export const AWAY_MS = 30_000

// Render ids this tab has shown, and when each last unmounted. Module scope:
// it outlives the page's own mount, which is the point.
const shown = new Set<string>()
const unmountedAt = new Map<string, number>()

/** Test-only. */
export function __resetRefreshOnReturn(): void {
  shown.clear()
  unmountedAt.clear()
}

export default function RefreshOnReturn({ renderId }: { renderId: string }) {
  const router = useRouter()

  // 1. A replayed render (browser Back/Forward through the router cache).
  useEffect(() => {
    if (shown.has(renderId)) {
      const gone = unmountedAt.get(renderId)
      // React StrictMode (dev) unmounts and remounts in the same tick — that
      // is not a return, so don't refresh for it.
      if (gone == null || performance.now() - gone > 50) router.refresh()
    } else {
      shown.add(renderId)
    }
    return () => {
      unmountedAt.set(renderId, performance.now())
    }
  }, [renderId, router])

  // 2 + 3. Full-page restore, and coming back after being away.
  useEffect(() => {
    let awaySince: number | null = null
    const leave = () => {
      if (awaySince == null) awaySince = Date.now()
    }
    const back = () => {
      if (document.visibilityState !== 'visible') return
      const since = awaySince
      awaySince = null
      if (since != null && Date.now() - since >= AWAY_MS) router.refresh()
    }
    const onVisibility = () => (document.visibilityState === 'hidden' ? leave() : back())
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) router.refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('blur', leave)
    window.addEventListener('focus', back)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('blur', leave)
      window.removeEventListener('focus', back)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [router])

  return null
}
